import type { captureException, captureMessage, Scope } from '@sentry/browser';

import { SENTRY_DSN, UNKNOWN_TAG } from '../../constants';

type SentryClient = {
	captureException: typeof captureException;
	captureMessage: typeof captureMessage;
	withScope(callback: (scope: Scope) => void): void;
};

let Sentry: Promise<SentryClient | void> | undefined;

export const getEnvironment = (): 'development' | 'staging' | 'production' => {
	if (window.location.hostname.includes('localhost')) {
		return 'development';
	} else if (
		window.location.hostname.includes('jira-dev.com') ||
		window.location.hostname.includes('stg.atlassian.com')
	) {
		return 'staging';
	} else {
		return 'production';
	}
};

// @ts-ignore - extra properties on window are not known
export const PRODUCT_BUILD: any = window.BUILD_KEY;

export const getSentryClient = async (): Promise<void | SentryClient> => {
	if (!Sentry) {
		Sentry = import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/dynamic-cards-search-common/sentry" */ '@sentry/browser'
		)
			.then((SentryModule) => {
				SentryModule.init({
					dsn: SENTRY_DSN,
					attachStacktrace: true,
					release: PRODUCT_BUILD || UNKNOWN_TAG,
					environment: getEnvironment(),
				});

				return SentryModule;
			})
			.catch(() => {});
	}

	return Sentry;
};

export const logException = async (error: any): Promise<void> => {
	try {
		const SentryClient = await getSentryClient();

		if (!SentryClient) {
			return;
		}

		SentryClient.withScope((scope) => {
			scope.setTag('product', 'hub');
			SentryClient.captureException(error, scope);
		});
	} catch (error) {
		// When there was an error with Sentry, just output to console
		// eslint-disable-next-line no-console
		console.error(error);
	}
};
